<template>
    <div class="tools-container">

        <div class="tools-grid" :class="{ 'tool-expanded': selectedTool }">
            <TransitionGroup 
                name="tool-move"
                tag="div"
                class="tools-wrapper"
            >
                <div v-for="tool in sortedTools" 
                    :key="tool.id" 
                    class="tool-card" 
                    :class="{ 'is-expanded': selectedTool === tool.id }"
                    @click="handleToolCardClick(tool)">
                    <div class="tool-header">
                        <el-image 
                            class="tool-icon"
                            fit="cover"
                        >
                            <template #placeholder>
                                <div class="image-placeholder">
                                    <el-icon class="is-loading"><Loading /></el-icon>
                                </div>
                            </template>
                            <template #error>
                                <div class="image-error">
                                    <el-icon><Tools /></el-icon>
                                </div>
                            </template>
                        </el-image>
                        <div class="tool-info">
                            <h3>{{ tool.name }}</h3>
                            <p>{{ tool.description }}</p>
                        </div>
                        <el-button
                            v-if="selectedTool === tool.id"
                            class="collapse-button"
                            type="text"
                            @click.stop="collapseToolCard"
                        >
                            收起
                        </el-button>
                    </div>
                    <div v-if="selectedTool === tool.id" class="tool-content" @click.stop>
                        <component :is="tool.component" v-if="tool.component" />
                        <template v-else>
                            <p>还在努力开发中</p>
                        </template>
                    </div>
                </div>
            </TransitionGroup>
        </div>
    </div>
</template>

<script>
import { Loading, Tools, Close } from '@element-plus/icons-vue'
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import ReputationCalculator from '@/components/tools/ReputationCalculator.vue'
import BatchRegister from '@/components/tools/BatchRegister.vue'

export default {
    name: 'ToolsPage',
    components: {
        Loading,
        Tools,
        Close,
        ReputationCalculator,
        BatchRegister
    },
    setup() {
        const selectedTool = ref(null);
        const isMobile = ref(false);
        
        const tools = [
            {
                id: 1,
                name: '声望计算',
                description: '快速计算声望所需智慧及价格',
                component: ReputationCalculator
            },
            {
                id: 2,
                name: '小号注册',
                description: '批量小号注册',
                component: BatchRegister
            },
            {
                id: 3,
                name: '养殖计算',
                description: '快速计算养殖经验',
                component: null
            }
        ];

        const handleToolCardClick = (tool) => {
            if (selectedTool.value === null) {
                selectedTool.value = tool.id;
            }
        };

        const collapseToolCard = () => {
            selectedTool.value = null;
        };

        const checkMobile = () => {
            isMobile.value = window.innerWidth <= 768;
        };

        onMounted(() => {
            checkMobile();
            window.addEventListener('resize', checkMobile);
        });

        onBeforeUnmount(() => {
            window.removeEventListener('resize', checkMobile);
        });

        return {
            selectedTool,
            tools,
            isMobile,
            handleToolCardClick,
            collapseToolCard,
            sortedTools: computed(() => {
                if (!selectedTool.value || isMobile.value) return tools;
                return [...tools].sort((a, b) => {
                    if (a.id === selectedTool.value) return -1;
                    if (b.id === selectedTool.value) return 1;
                    return 0;
                });
            })
        };
    }
}
</script>

<style scoped>
.tools-container {
    max-width: 1200px;
    width: 90%;
    margin: 0 auto;
    padding: 80px 0;
    overflow: hidden;
}


.tools-grid {
    position: relative;
    width: 100%;
}

.tools-wrapper {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    width: 100%;
}

.tool-move-move {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.tools-grid.tool-expanded .tool-card:not(.is-expanded) {
    opacity: 0.6;
    transform: scale(0.95);
}

.tool-card {
    background: var(--adil-card-bg);
    border-radius: 12px;
    padding: 24px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(0, 122, 255, 0.1);
    grid-column: span 1;
    transform-origin: center;
}

.tool-card.is-expanded {
    grid-column: 1 / -1;
    grid-row: span 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    transform: none;
}

.tool-card.is-expanded .tool-header {
    margin-bottom: 0;
}

.tool-card.is-expanded .tool-icon {
    margin-bottom: 0;
}

.tool-card.is-expanded .tool-info {
    text-align: left;
}

.tool-card.is-expanded .tool-content {
    grid-column: 1 / -1;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid rgba(0, 122, 255, 0.1);
}

.tool-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px -4px rgba(0, 122, 255, 0.15);
}

.tool-card.is-expanded:hover {
    transform: none;
}

.tool-icon {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
}

.tool-info {
    flex: 1;
}

.tool-info h3 {
    font-size: 18px;
    color: var(--adil-fontcolor);
    margin: 0 0 8px;
}

.tool-info p {
    font-size: 14px;
    color: var(--adil-secondtext);
    margin: 0;
}

.collapse-button {
    position: absolute;
    right: 0;
    top: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--adil-secondtext);
    font-size: 14px;
    padding: 4px 8px;
}

.image-placeholder,
.image-error {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--el-fill-color-light);
    border-radius: 8px;
}

.tool-content {
    width: 100%;
}

@media (max-width: 1024px) {
    .tools-wrapper {
        grid-template-columns: repeat(2, 1fr);
    }

    .tool-move-move {
        width: calc((100% - 30px) / 2);
    }

    .tool-card.is-expanded {
        grid-template-columns: 48px 1fr;
        gap: 16px;
    }
}

@media (max-width: 768px) {
    .tools-container {
        padding: 60px 0;
    }

    .tools-wrapper {
        grid-template-columns: 1fr;
    }

    .tool-move-move {
        width: 100%;
    }


    .tool-card.is-expanded {
        grid-template-columns: 1fr;
    }

    .tool-card.is-expanded .tool-icon {
        margin-bottom: 16px;
    }
}

.tool-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    position: relative;
}
</style> 